import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { accountApi } from '../services/accountApi';
import { BaseUser } from '../dtos/Users/index';
import { LoginRequest } from '../dtos/Accounts/index';

interface AuthContextType {
  user: BaseUser | null;
  login: (data: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [user, setUser] = useState<BaseUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check auth status on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await accountApi.getProfile();
        if (response.isSuccess && response.data) {
          setUser(response.data);
        }
      } catch (error) {
        // Not authenticated, that's fine
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = useCallback(async (data: LoginRequest) => {
    const response = await accountApi.login(data);

    if (response.isSuccess) {
      // Fetch profile after successful login
      const profileResponse = await accountApi.getProfile();
      if (profileResponse.isSuccess && profileResponse.data) {
        setUser(profileResponse.data);
      }
      // Don't navigate here - let the Login page handle it
    } else {
      throw new Error(response.errorMessage || 'Login failed');
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await accountApi.logout();
    } catch {
      // Ignore logout errors
    }
    setUser(null);
    queryClient.clear();
    navigate('/login', { replace: true });
  }, [queryClient, navigate]);

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        isLoading,
        isAuthenticated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
